import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { OfficialReceipt, Order } from "@ai-pos/shared";
import { ordersApi, receiptsApi, settingsApi } from "../api/resources.js";
import { ReceiptIssueModal } from "../components/ReceiptIssueModal.js";
import { useAuthStore } from "../stores/authStore.js";

export function SalesDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const staff = useAuthStore((state) => state.staff);
  // 会計の取消は現金や売上の改ざんに直結するため、一般スタッフには見せない（サーバー側でも検証済み）。
  const canVoid = staff?.role === "ADMIN" || staff?.role === "OWNER";
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [showVoidForm, setShowVoidForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receipts, setReceipts] = useState<OfficialReceipt[]>([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptDefaultDescription, setReceiptDefaultDescription] = useState("お品代として");

  const load = () => {
    if (!id) return;
    ordersApi
      .get(id)
      .then((res) => setOrder(res.order))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "取得に失敗しました"));
    receiptsApi
      .list(id)
      .then((res) => setReceipts(res.receipts))
      .catch(() => undefined);
  };

  useEffect(load, [id]);

  useEffect(() => {
    settingsApi
      .getPublic()
      .then((res) => setReceiptDefaultDescription(res.officialReceiptDefaultDescription))
      .catch(() => undefined);
  }, []);

  if (!order) {
    return (
      <div className="stack">
        {error && <div className="alert alert-error">{error}</div>}
        {!error && <div>読み込み中...</div>}
      </div>
    );
  }

  const handleReprint = async () => {
    if (!id) return;
    setBusy(true);
    try {
      const res = await ordersApi.reprint(id);
      setMessage(`【再印刷】${res.printResult.message}`);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "再印刷に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  const handleVoid = async () => {
    if (!id || !voidReason.trim()) return;
    setBusy(true);
    try {
      const res = await ordersApi.void(id, voidReason);
      setOrder(res.order);
      setShowVoidForm(false);
      setVoidReason("");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "取消に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <button className="btn" onClick={() => navigate(-1)}>
        戻る
      </button>
      <h2>会計詳細 {order.receiptNumber}</h2>
      {order.status === "VOIDED" && order.voidRecord && (
        <div className="alert alert-error">
          この会計は取消済みです（取消日時: {new Date(order.voidRecord.voidedAt).toLocaleString("ja-JP")} / 担当:{" "}
          {order.voidRecord.voidedByStaffName} / 理由: {order.voidRecord.reason}）
        </div>
      )}
      {message && <div className="alert alert-warning">{message}</div>}

      <div className="card stack">
        <div>日時: {new Date(order.orderedAt).toLocaleString("ja-JP")}</div>
        <div>担当: {order.staffName}</div>
        <div>人数: {order.guestCount}名</div>
        <div>
          支払方法:{" "}
          {order.payments.map((payment, index) => (
            <span key={payment.paymentMethodId}>
              {index > 0 && "、"}
              {payment.paymentMethodName} ¥{payment.amount.toLocaleString("ja-JP")}
            </span>
          ))}
        </div>
        <table>
          <thead>
            <tr>
              <th>商品</th>
              <th>数量</th>
              <th>単価</th>
              <th>小計</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((item) => (
              <tr key={item.id}>
                <td>{item.productName}</td>
                <td>{item.quantity}</td>
                <td>¥{item.unitPrice.toLocaleString("ja-JP")}</td>
                <td>¥{item.subtotal.toLocaleString("ja-JP")}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="cart-summary">
          <div className="cart-summary__row">
            <span>小計</span>
            <span>¥{order.subtotal.toLocaleString("ja-JP")}</span>
          </div>
          <div className="cart-summary__row">
            <span>値引き</span>
            <span>¥{order.discountTotal.toLocaleString("ja-JP")}</span>
          </div>
          <div className="cart-summary__row cart-summary__total">
            <span>合計</span>
            <span>¥{order.total.toLocaleString("ja-JP")}</span>
          </div>
        </div>
      </div>

      <div className="row">
        <button className="btn" onClick={() => void handleReprint()} disabled={busy}>
          再印刷
        </button>
        {order.status !== "VOIDED" && canVoid && (
          <button className="btn btn-danger" onClick={() => setShowVoidForm(true)} disabled={busy}>
            取消
          </button>
        )}
        {order.status !== "VOIDED" && (
          <button className="btn btn-primary" onClick={() => setShowReceiptModal(true)} disabled={busy}>
            領収書発行
          </button>
        )}
      </div>

      <div className="card">
        <h3>領収書発行履歴</h3>
        {receipts.length === 0 && <div className="empty-state">発行履歴はありません</div>}
        {receipts.map((receipt) => (
          <div key={receipt.id} className="cart-item">
            <span className="cart-item__name">
              {receipt.recipientName || "上様"} / {receipt.description}
              {receipt.isReissue && <span className="badge" style={{ marginLeft: 6 }}>再発行</span>}
            </span>
            <span>{new Date(receipt.createdAt).toLocaleString("ja-JP")}</span>
            {receipt.printStatus === "SUCCESS" ? (
              <span className="badge">印刷成功</span>
            ) : (
              <span className="badge badge-danger">印刷失敗</span>
            )}
          </div>
        ))}
      </div>

      {showVoidForm && (
        <div className="modal-backdrop" onClick={() => setShowVoidForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>会計取消</h3>
            <div className="form-field">
              <label htmlFor="void-reason">取消理由（必須）</label>
              <textarea id="void-reason" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
            </div>
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn" onClick={() => setShowVoidForm(false)}>
                キャンセル
              </button>
              <button className="btn btn-danger" onClick={() => void handleVoid()} disabled={!voidReason.trim() || busy}>
                取消を確定
              </button>
            </div>
          </div>
        </div>
      )}

      {showReceiptModal && id && (
        <ReceiptIssueModal
          orderId={id}
          defaultDescription={receiptDefaultDescription}
          onClose={() => setShowReceiptModal(false)}
          onIssued={(result) => {
            setReceipts((prev) => [result.receipt, ...prev]);
            setMessage(`【領収書】${result.printResult.message}`);
            setShowReceiptModal(false);
          }}
        />
      )}
    </div>
  );
}
